import { useEffect, useRef, useState } from 'react';
import { ensureModelViewer } from '@/lib/ensureModelViewer';
import { isNativeApp } from '@/lib/platform';

// TypeScript declaration for <model-viewer> web component
declare global {
  namespace JSX {
    interface IntrinsicElements {
      "model-viewer": React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        src?: string;
        alt?: string;
        "auto-rotate"?: boolean | string;
        "camera-controls"?: boolean | string;
        "shadow-intensity"?: string;
        exposure?: string;
        "interaction-prompt"?: string;
        "camera-orbit"?: string;
      };
    }
  }
}

interface DignityAvatarProps {
  size: 'chat' | 'static';
  isSpeaking?: boolean;
  className?: string;
}

export function DignityAvatar({ size, isSpeaking = false, className = '' }: DignityAvatarProps) {
  const viewerRef = useRef<HTMLElement>(null);
  const [modelReady, setModelReady] = useState(false);
  const animRef = useRef<number>(0);

  // Hardware-acceleration probe mirroring DignityEducationTab: a viewport-width
  // gate alone wasn't enough — VMs / spec'd machines with `llvmpipe` / software
  // WebGL render the GLB at ~5 fps and sometimes crash the renderer entirely.
  // Falling back to the static PNG on those hosts keeps the page snappy and
  // matches the avatar treatment used elsewhere on the site.
  const hasWebGL = typeof window !== 'undefined' && (() => {
    try {
      const c = document.createElement('canvas');
      const gl = (c.getContext('webgl2') || c.getContext('webgl')) as WebGLRenderingContext | null;
      if (!gl) return false;
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      if (debugInfo) {
        const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string;
        if (/llvmpipe|swiftshader|software/i.test(renderer)) return false;
      }
      return true;
    } catch { return false; }
  })();
  // Use the 3D GLB on desktop OR in the native app (iOS/Android). The old
  // `innerWidth >= 768` gate forced the flat PNG on all phone widths, so the
  // native app never showed Dignity's GLB form — only the fallback image.
  // WKWebView has hardware WebGL and the model-viewer chunk + dignity_bill.glb
  // are bundled locally, so the GLB renders offline. hasWebGL still guards
  // software renderers (VMs) that would crawl.
  const useGlb = size === 'chat' && hasWebGL &&
    (isNativeApp() || (typeof window !== 'undefined' && window.innerWidth >= 768));

  useEffect(() => {
    if (!useGlb) return;
    const load = async () => {
      try {
        if (!(typeof customElements !== 'undefined' && customElements.get('model-viewer'))) {
          await ensureModelViewer();
          await customElements.whenDefined('model-viewer');
        }
        // Smoke-test that the custom element can actually create a renderer.
        // Catches the "WebGL context lost" failure path that customElements
        // resolution alone doesn't expose.
        const testEl = document.createElement('model-viewer') as any;
        testEl.style.cssText = 'width:1px;height:1px;position:absolute;left:-9999px';
        document.body.appendChild(testEl);
        await new Promise((r) => setTimeout(r, 200));
        document.body.removeChild(testEl);
        setModelReady(true);
      } catch { /* PNG fallback below handles it */ }
    };
    load();
  }, [useGlb]);

  // Oscillating head animation
  useEffect(() => {
    if (!useGlb || !modelReady) return;
    const amplitude = isSpeaking ? 20 : 12;
    const period = isSpeaking ? 3000 : 5000;
    let start = performance.now();

    function animate(now: number) {
      const elapsed = now - start;
      const theta = amplitude * Math.sin((2 * Math.PI * elapsed) / period);
      const viewer = viewerRef.current as any;
      if (viewer && viewer.cameraOrbit !== undefined) {
        viewer.cameraOrbit = `${theta}deg 75deg 105%`;
      }
      animRef.current = requestAnimationFrame(animate);
    }
    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
  }, [useGlb, modelReady, isSpeaking]);

  // Static PNG fallback (software-WebGL hosts or explicit static size). Sized to
  // match the GLB (80px) so the fallback doesn't render as a tiny 48px thumbnail
  // in the tall hub avatar column.
  if (!useGlb) {
    const px = 80;
    return (
      <div
        className={`rounded-full overflow-hidden bg-amber-50 shrink-0 ${className}`}
        style={{ width: px, height: px }}
      >
        <img
          src="/consumer/dignity-transparent.webp"
          alt="Dignity"
          className="w-full h-full object-cover object-top"
          loading="lazy"
        />
      </div>
    );
  }

  // 3D model-viewer (desktop chat size)
  const px = 80;
  return (
    <div
      className={`rounded-full overflow-hidden shrink-0 ${isSpeaking ? 'ring-2 ring-amber-400/50 ring-offset-1' : ''} ${className}`}
      style={{ width: px, height: px }}
    >
      {modelReady ? (
        <model-viewer
          ref={viewerRef as any}
          src="/consumer/dignity_bill.glb"
          alt="Dignity"
          shadow-intensity="0.8"
          exposure="1.1"
          interaction-prompt="none"
          camera-orbit="0deg 75deg 105%"
          style={{
            width: px + 20,
            height: px + 20,
            marginTop: -6,
            marginLeft: -10,
            background: 'transparent',
          }}
        />
      ) : (
        <div className="w-full h-full bg-amber-100 animate-pulse" />
      )}
    </div>
  );
}
